/* eslint-disable @typescript-eslint/no-explicit-any */

const ASPECT = { TD3: 125 / 88, TD1: 85.6 / 54 } as const;

/**
 * Applies a perspective warp to straighten the detected document quad into a
 * normalised rectangle.
 *
 * @param cv           Loaded OpenCV.js instance.
 * @param sourceCanvas Full-resolution capture canvas (videoGuide crop).
 * @param geomCorners  Quad corners in the geometry-detection canvas space (TL TR BR BL).
 * @param geomW        Width of the geometry detection canvas.
 * @param geomH        Height of the geometry detection canvas.
 * @param documentType Used to determine the target aspect ratio.
 * @returns Corrected canvas, or null if correction failed or was skipped.
 */
export function applyPerspectiveCorrection(
  cv: any,
  sourceCanvas: HTMLCanvasElement,
  geomCorners: readonly [number, number][],
  geomW: number,
  geomH: number,
  documentType: "TD1" | "TD3",
): HTMLCanvasElement | null {
  const sw = sourceCanvas.width;
  const sh = sourceCanvas.height;

  // Scale quad corners from geom canvas space → source canvas space.
  const scaleX = sw / geomW;
  const scaleY = sh / geomH;
  const [tl, tr, br, bl] = geomCorners.map(([px, py]) => [
    px * scaleX,
    py * scaleY,
  ]);

  // Determine output dimensions from average detected side lengths.
  const topW = Math.hypot(tr[0] - tl[0], tr[1] - tl[1]);
  const botW = Math.hypot(br[0] - bl[0], br[1] - bl[1]);
  const leftH = Math.hypot(bl[0] - tl[0], bl[1] - tl[1]);
  const rightH = Math.hypot(br[0] - tr[0], br[1] - tr[1]);

  const avgW = (topW + botW) / 2;
  const avgH = (leftH + rightH) / 2;

  // Sanity check — if the detected geometry is degenerate, skip correction.
  if (avgW < 20 || avgH < 20) return null;

  // Clamp output to the source canvas size, maintaining the expected aspect ratio.
  const expectedAspect = ASPECT[documentType];
  let dstW = Math.round(Math.min(avgW, sw));
  let dstH = Math.round(dstW / expectedAspect);
  if (dstH > sh) {
    dstH = sh;
    dstW = Math.round(dstH * expectedAspect);
  }

  const srcCtx = sourceCanvas.getContext("2d", { willReadFrequently: true });
  if (!srcCtx) return null;
  const srcImageData = srcCtx.getImageData(0, 0, sw, sh);

  const srcMat = cv.matFromImageData(srcImageData);
  const dstMat = new cv.Mat();

  const srcPts = cv.matFromArray(4, 1, cv.CV_32FC2, [
    tl[0], tl[1],
    tr[0], tr[1],
    br[0], br[1],
    bl[0], bl[1],
  ]);
  const dstPts = cv.matFromArray(4, 1, cv.CV_32FC2, [
    0,       0,
    dstW - 1, 0,
    dstW - 1, dstH - 1,
    0,       dstH - 1,
  ]);

  try {
    const M = cv.getPerspectiveTransform(srcPts, dstPts);
    cv.warpPerspective(
      srcMat,
      dstMat,
      M,
      new cv.Size(dstW, dstH),
      cv.INTER_LINEAR,
      cv.BORDER_CONSTANT,
      new cv.Scalar(),
    );
    M.delete();

    const out = document.createElement("canvas");
    out.width = dstW;
    out.height = dstH;
    cv.imshow(out, dstMat);
    return out;
  } catch {
    return null;
  } finally {
    srcMat.delete();
    dstMat.delete();
    srcPts.delete();
    dstPts.delete();
  }
}
